import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePDIContext } from '../context/PDIContext';
import { usePageMeta } from '../hooks/usePageMeta';
import VINDecoder from '../components/VINDecoder';
import OdometerChecker from '../components/OdometerChecker';
import FeatureSelector from '../components/FeatureSelector';
import {
  Car,
  Calendar,
  MapPin,
  Hash,
  Palette,
  Building2,
  User,
  Save,
  Trash2,
  Sparkles,
  Shield,
  FileText,
  ArrowRight,
} from 'lucide-react';

const CarProfilePage = () => {
  const navigate = useNavigate();
  const { carProfile, updateCarProfile } = usePDIContext();
  const [selectedFeatures, setSelectedFeatures] = useState(carProfile?.features || []);
  const [saved, setSaved] = useState(false);

  usePageMeta({
    title: 'Car Profile Setup - VIN Decoder & Odometer Checker',
    description: 'Decode your VIN to check manufacture date, verify the odometer reading, and select your car features to tailor your PDI checklist.',
    path: '/car-profile',
  });

  const [formData, setFormData] = useState({
    carMake: '',
    carModel: '',
    variant: '',
    color: '',
    vin: '',
    engineNumber: '',
    dealerName: '',
    dealerLocation: '',
    salesPerson: '',
    inspectionDate: '',
    notes: '',
  });

  useEffect(() => {
    if (carProfile) {
      setFormData({
        carMake: carProfile.carMake || '',
        carModel: carProfile.carModel || '',
        variant: carProfile.variant || '',
        color: carProfile.color || '',
        vin: carProfile.vin || '',
        engineNumber: carProfile.engineNumber || '',
        dealerName: carProfile.dealerName || '',
        dealerLocation: carProfile.dealerLocation || '',
        salesPerson: carProfile.salesPerson || '',
        inspectionDate: carProfile.inspectionDate || '',
        notes: carProfile.notes || '',
      });
      setSelectedFeatures(carProfile.features || []);
    }
  }, [carProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    updateCarProfile({ ...formData, features: selectedFeatures });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleFeaturesChange = (features) => {
    setSelectedFeatures(features);
    updateCarProfile({ ...carProfile, features });
    setSaved(false);
  };

  const handleClear = () => {
    const emptyData = {
      carMake: '',
      carModel: '',
      variant: '',
      color: '',
      vin: '',
      engineNumber: '',
      dealerName: '',
      dealerLocation: '',
      salesPerson: '',
      inspectionDate: '',
      notes: '',
    };
    setFormData(emptyData);
    setSelectedFeatures([]);
    updateCarProfile(emptyData);
  };

  const inputGroups = [
    {
      title: 'Vehicle Information',
      icon: Car,
      gradient: 'from-blue-400 to-indigo-500',
      bgLight: 'from-blue-50 to-indigo-50',
      fields: [
        { name: 'carMake', label: 'Make', placeholder: 'e.g., Toyota, Honda, Hyundai', icon: Car },
        { name: 'carModel', label: 'Model', placeholder: 'e.g., Camry, City, Creta', icon: Car },
        { name: 'variant', label: 'Variant', placeholder: 'e.g., VX, ZX, SX(O)', icon: Car },
        { name: 'color', label: 'Color', placeholder: 'e.g., Pearl White, Midnight Black', icon: Palette },
      ],
    },
    {
      title: 'Identification Numbers',
      icon: Hash,
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'from-emerald-50 to-teal-50',
      fields: [
        { name: 'vin', label: 'VIN Number', placeholder: '17-character Vehicle Identification Number', icon: Hash },
        { name: 'engineNumber', label: 'Engine Number', placeholder: 'Found on engine block', icon: Hash },
      ],
    },
    {
      title: 'Dealer Information',
      icon: Building2,
      gradient: 'from-violet-400 to-purple-500',
      bgLight: 'from-violet-50 to-purple-50',
      fields: [
        { name: 'dealerName', label: 'Dealer Name', placeholder: 'e.g., ABC Motors Pvt Ltd', icon: Building2 },
        { name: 'dealerLocation', label: 'Location', placeholder: 'e.g., MG Road, Bangalore', icon: MapPin },
        { name: 'salesPerson', label: 'Sales Person', placeholder: 'Contact person name', icon: User },
      ],
    },
    {
      title: 'Inspection Details',
      icon: Calendar,
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'from-amber-50 to-orange-50',
      fields: [
        { name: 'inspectionDate', label: 'Inspection Date', type: 'date', icon: Calendar },
      ],
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="relative text-center mb-8 hero-pattern -mx-4 px-4 pt-8 pb-8 -mt-4">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-50/80" />
        <div className="relative">
          <div className="w-20 h-20 rounded-2xl gradient-animated flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Car className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-800">
            Car Profile
          </h1>
          <p className="text-slate-500 text-sm mt-2 max-w-sm mx-auto">
            Save details about your car for reference and PDF export
          </p>
        </div>
      </div>

      {/* Feature Selector - shapes the checklist, so it comes first */}
      <div className="mb-6">
        <FeatureSelector
          selectedFeatures={selectedFeatures}
          onFeaturesChange={handleFeaturesChange}
        />
      </div>

      {/* VIN Decoder */}
      <div className="mb-6">
        <VINDecoder />
      </div>

      {/* Odometer Checker */}
      <div className="mb-6">
        <OdometerChecker />
      </div>

      {/* Form Groups */}
      <div className="space-y-5">
        {inputGroups.map((group, groupIndex) => {
          const GroupIcon = group.icon;
          return (
            <div 
              key={group.title}
              className="bg-white rounded-3xl border-2 border-slate-200 shadow-sm overflow-hidden animate-slide-up hover:shadow-md transition-shadow"
              style={{ animationDelay: `${groupIndex * 50}ms` }}
            >
              <div className={`px-5 py-4 border-b border-slate-100 bg-gradient-to-r ${group.bgLight}`}>
                <h2 className="font-bold text-slate-700 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${group.gradient} flex items-center justify-center shadow-lg`}>
                    <GroupIcon className="w-4 h-4 text-white" />
                  </div>
                  {group.title}
                </h2>
              </div>
              <div className="p-5 space-y-4">
                {group.fields.map(field => {
                  const FieldIcon = field.icon;
                  return (
                    <div key={field.name}>
                      <label className="block text-sm font-semibold text-slate-600 mb-2">
                        {field.label}
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                          <FieldIcon className="w-4 h-4 text-slate-400" />
                        </div>
                        <input
                          type={field.type || 'text'}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          placeholder={field.placeholder}
                          className="pl-14 rounded-xl"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Notes */}
        <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-sm overflow-hidden animate-slide-up hover:shadow-md transition-shadow">
          <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-gray-50">
            <h2 className="font-bold text-slate-700 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-gray-600 flex items-center justify-center shadow-lg">
                <FileText className="w-4 h-4 text-white" />
              </div>
              Additional Notes
            </h2>
          </div>
          <div className="p-5">
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any additional notes about the car or inspection..."
              rows={4}
              className="resize-none rounded-xl"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-8 space-y-3">
        <button
          onClick={handleSave}
          className={`w-full font-bold py-4 px-6 rounded-2xl shadow-xl btn-touch flex items-center justify-center gap-3 text-lg transition-all ${
            saved 
              ? 'bg-gradient-to-r from-brand-500 to-emerald-500 text-white shadow-brand-500/30' 
              : 'gradient-animated text-white shadow-brand-500/25'
          }`}
        >
          {saved ? (
            <>
              <Sparkles className="w-5 h-5" />
              Saved Successfully!
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Car Profile
            </>
          )}
        </button>
        
        <button
          onClick={() => {
            updateCarProfile({ ...formData, features: selectedFeatures });
            navigate('/checklist');
          }}
          className="w-full bg-slate-900 text-white font-bold py-4 px-6 rounded-2xl shadow-xl btn-touch flex items-center justify-center gap-3 text-lg hover:bg-slate-800 transition-colors"
        >
          Continue to Inspection
          <ArrowRight className="w-5 h-5" />
        </button>

        <button
          onClick={handleClear}
          className="w-full bg-white border-2 border-slate-200 text-slate-600 font-semibold py-3.5 px-6 rounded-2xl btn-touch flex items-center justify-center gap-2 hover:border-danger-300 hover:text-danger-600 transition-colors"
        >
          <Trash2 className="w-5 h-5" />
          Clear Profile
        </button>
      </div>

      {/* Info Note */}
      <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400">
        <Shield className="w-4 h-4" />
        <span>All data is stored locally on your device only</span>
      </div>
    </div>
  );
};

export default CarProfilePage;
